import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useMessagesStore } from "@/atoms/playground";
import { useAuth } from "@/contexts/AuthContext";
import { conversationApi } from "@/services/conversation-service";

type Feedback = {
  id: string;
  message_id: string;
  user_id: string;
  value: boolean;
  created_at: string;
  updated_at: string;
};

type Props = {
  messageId: string;
  conversationId: string;
  streaming: boolean;
  initialFeedback: boolean | null;
};

export function MessageFeedback({
  messageId,
  conversationId,
  streaming,
  initialFeedback,
}: Props) {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const client = useQueryClient();
  const submitting = useRef(false);
  const [selectedValue, setSelectedValue] = useState<boolean | null>(
    initialFeedback,
  );
  const ready = Boolean(
    currentUser?.id &&
      conversationId &&
      messageId &&
      !messageId.startsWith("temp-") &&
      !streaming,
  );

  useEffect(() => {
    setSelectedValue(initialFeedback);
  }, [initialFeedback, messageId]);

  const saveFeedbackInMessage = (value: boolean | null) => {
    setSelectedValue(value);

    const messageState = useMessagesStore.getState();
    messageState.setMessages(
      messageState.messages.map((message) =>
        message.id === messageId ? { ...message, feedback: value } : message,
      ),
    );

    client.setQueriesData(
      { queryKey: ["conversation", conversationId] },
      (conversation: any) => {
        if (!Array.isArray(conversation?.messages)) return conversation;
        return {
          ...conversation,
          messages: conversation.messages.map((message: any) =>
            message.id === messageId ? { ...message, feedback: value } : message,
          ),
        };
      },
    );
  };

  const mutation = useMutation({
    mutationFn: async (value: boolean) => {
      const { data } = await conversationApi.post<Feedback | null>(
        "/conversations/messages/feedback",
        { message_id: messageId, value },
      );
      return data;
    },
    retry: false,
    onSuccess: (data) => saveFeedbackInMessage(data?.value ?? null),
    onSettled: () => {
      submitting.current = false;
    },
  });

  const disabled = !ready || mutation.isPending;
  const submit = (value: boolean) => {
    if (disabled || submitting.current) return;
    submitting.current = true;
    mutation.mutate(value);
  };

  return (
    <div
      className="flex flex-wrap items-center gap-1"
      role="group"
      aria-label={t("playground.feedback.label")}
      aria-busy={mutation.isPending}
    >
      {[true, false].map((value) => {
        const selected = selectedValue === value;
        const Icon = value ? ThumbsUp : ThumbsDown;
        const label = t(
          value ? "playground.feedback.up" : "playground.feedback.down",
        );
        return (
          <Button
            className="enabled:cursor-pointer"
            key={String(value)}
            type="button"
            size="icon-sm"
            variant={selected ? "secondary" : "ghost"}
            aria-label={label}
            title={label}
            aria-pressed={selected}
            disabled={disabled}
            onClick={() => submit(value)}
          >
            <Icon className={selected ? "fill-current" : ""} aria-hidden="true" />
          </Button>
        );
      })}
      {mutation.isError && (
        <span role="alert" className="text-xs text-destructive">
          {t("playground.feedback.saveError")}
        </span>
      )}
    </div>
  );
}